import type { JSX } from 'react';
import { makeStyles } from '@fluentui/react-components';
import { useThemeMode } from '@/hooks/useThemeMode';

/**
 * Logos de MARQUE des services tiers (SharePoint, Teams, Autodesk), pour le
 * sous-menu « Espaces collaboratifs ».
 *
 * POURQUOI DES IMAGES ET NON DES ICÔNES FLUENT. Ces entrées font quitter l'add-in
 * pour un autre outil. Un pictogramme générique (un document, un groupe de
 * personnes, un immeuble) décrit la NATURE de la destination ; le logo dit
 * laquelle. Dans un menu où trois lignes sur quatre mènent ailleurs, c'est la
 * seule distinction qui se lit d'un coup d'œil. La fiche PRISM garde son icône
 * Fluent : c'est notre outil, pas une marque tierce.
 *
 * SERVIS DEPUIS `public/brand/`, référencés par chemin absolu, comme les icônes du
 * manifeste (`assets/icons/`) — pas d'import bundlé : ce sont des fichiers
 * statiques que le navigateur met en cache une fois pour toutes, et le volet est
 * démonté/remonté à chaque changement de mail sélectionné.
 *
 * REDIMENSIONNÉS À 64 px À LA SOURCE. Les fichiers d'origine faisaient 500×489 à
 * 3840×4054, dont **1 Mo pour le seul logo Teams** — pour un glyphe de 20 px. Un
 * navigateur aurait décodé 15 mégapixels par ouverture de menu. Les quatre pèsent
 * désormais 12 Ko au total, contre 1,1 Mo. Les originaux sont conservés hors du
 * dossier servi, dans `_brand-src/`.
 */
const useStyles = makeStyles({
  // 20 px = la taille des icônes Fluent des autres entrées du menu, sans quoi les
  // libellés ne s'aligneraient plus entre elles. `objectFit: contain` protège les
  // logos non carrés (Teams est en 61×64) d'un étirement.
  icon: {
    width: '20px',
    height: '20px',
    objectFit: 'contain',
    flexShrink: 0,
  },
});

type Brand = 'sharepoint' | 'teams' | 'autodesk';

/** Nom du fichier, `autodesk` étant le seul à dépendre du thème. */
function fileFor(brand: Brand, dark: boolean): string {
  if (brand !== 'autodesk') return `${brand}.png`;
  // Le logo Autodesk est MONOCHROME : il faut donc l'inverser, sinon il disparaît
  // dans le fond. Thème SOMBRE => variante BLANCHE ; thème CLAIR => variante NOIRE.
  // Le sens est facile à écrire à l'envers, et le défaut ne se verrait que sur un
  // seul des deux thèmes — d'où cette phrase plutôt qu'un ternaire nu.
  return dark ? 'autodesk-white.png' : 'autodesk-black.png';
}

const LABELS: Record<Brand, string> = {
  sharepoint: 'SharePoint',
  teams: 'Microsoft Teams',
  autodesk: 'Autodesk Construction Cloud',
};

export function BrandIcon({ brand }: { brand: Brand }): JSX.Element {
  const styles = useStyles();
  const dark = useThemeMode() === 'dark';
  return (
    <img
      className={styles.icon}
      src={`/brand/${fileFor(brand, dark)}`}
      // `alt` VIDE et `aria-hidden` : le libellé de l'entrée de menu dit déjà
      // « Espace documentaire SharePoint ». Un `alt` renseigné ferait annoncer la
      // marque deux fois de suite par un lecteur d'écran.
      alt=""
      aria-hidden
      title={LABELS[brand]}
      // Le logo ne porte aucune information que le libellé ne donne : il ne doit
      // pas laisser de trou pendant son chargement, ni décaler le texte.
      loading="eager"
      decoding="async"
    />
  );
}
